import { BACKEND_URL } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, Cloud, Sprout, TrendingUp, IndianRupee, Sparkles, RefreshCw, Home } from 'lucide-react';

const FarmInsights = ({ user, token, language }) => {
  const navigate = useNavigate();
  const [soilData, setSoilData] = useState(null);
  const [ndviData, setNdviData] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const t = {
    en: {
      title: 'Farm Insights',
      subtitle: 'Real-time agricultural data for smarter farming',
      soilHealth: 'Soil Health',
      cropHealth: 'Crop Health (NDVI)',
      marketPrices: 'Market Prices',
      aiRecommendation: 'AI Recommendation',
      refresh: 'Refresh Data',
      loading: 'Loading farm data...',
      backToDashboard: 'Back to Dashboard'
    },
    hi: {
      title: 'कृषि जानकारी',
      subtitle: 'स्मार्ट खेती के लिए वास्तविक समय कृषि डेटा',
      soilHealth: 'मिट्टी का स्वास्थ्य',
      cropHealth: 'फसल स्वास्थ्य (NDVI)',
      marketPrices: 'बाजार मूल्य',
      aiRecommendation: 'AI सिफारिश',
      refresh: 'डेटा रिफ्रेश करें',
      loading: 'कृषि डेटा लोड हो रहा है...',
      backToDashboard: 'डैशबोर्ड पर वापस जाएं'
    }
  };

  const text = language === 'hi' ? t.hi : t.en;

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [soilResponse, ndviResponse, marketResponse, aiResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/farm/soil-health`, { headers }),
        fetch(`${BACKEND_URL}/api/farm/ndvi`, { headers }),
        fetch(`${BACKEND_URL}/api/farm/market-prices`, { headers }),
        fetch(`${BACKEND_URL}/api/farm/ai-recommendation`, { headers }),
      ]);
      if (soilResponse.ok) setSoilData(await soilResponse.json());
      if (ndviResponse.ok) setNdviData(await ndviResponse.json());
      if (marketResponse.ok) setMarketData(await marketResponse.json());
      if (aiResponse.ok) setAiRecommendation(await aiResponse.json());
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching farm data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="farm-insights-page">
        <div className="loading-container">
          <Cloud size={48} className="spin" />
          <p>{text.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="farm-insights-page">
      {/* Header */}
      <header className="insights-header">
        <div className="back-nav-group">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={20} />
            {text.backToDashboard}
          </button>
          <button className="home-btn-alt" onClick={() => navigate('/')} title={language === 'hi' ? 'होम' : 'Home'}>
            <Home size={18} />
            {language === 'hi' ? 'होम' : 'Home'}
          </button>
        </div>
        <div className="header-content">
          <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={20} className={refreshing ? 'spin' : ''} />
            {text.refresh}
          </button>
        </div>
      </header>

      {/* Title */}
      <div className="insights-title-section">
        <h1>{text.title}</h1>
        <p>{text.subtitle}</p>
      </div>

      {/* Services Grid */}
      <div className="insights-grid">
        {/* Soil Health Card */}
        <div className="insight-card soil-card">
          <div className="card-header">
            <Sprout size={40} className="card-icon" />
            <h3>{text.soilHealth}</h3>
          </div>
          <div className="card-data">
            {soilData && (
              <>
                <div className="data-item">
                  <strong>pH:</strong> <span>{soilData.pH}</span>
                </div>
                <div className="data-item">
                  <strong>Carbon:</strong> <span>{soilData.organic_carbon}</span>
                </div>
                <div className="data-item">
                  <strong>Nitrogen:</strong> <span>{soilData.nitrogen}</span>
                </div>
              </>
            )}
          </div>
          <p className="card-description">
            {language === 'hi' ? soilData?.description_hi : soilData?.description_en}
          </p>
        </div>

        {/* NDVI/Crop Health Card */}
        <div className="insight-card ndvi-card">
          <div className="card-header">
            <Cloud size={40} className="card-icon" />
            <h3>{text.cropHealth}</h3>
          </div>
          <div className="card-data">
            {ndviData && (
              <>
                <div className="data-item">
                  <strong>NDVI:</strong> <span>{ndviData.NDVI}</span>
                </div>
                <div className="data-item">
                  <strong>Condition:</strong> 
                  <span className={`condition ${ndviData.condition.toLowerCase()}`}>
                    {language === 'hi' ? ndviData.condition_hi : ndviData.condition}
                  </span>
                </div>
              </>
            )}
          </div>
          <p className="card-description">
            {language === 'hi' ? ndviData?.description_hi : ndviData?.description_en}
          </p>
        </div>

        {/* Market Prices Card */}
        <div className="insight-card market-card">
          <div className="card-header">
            <IndianRupee size={40} className="card-icon" />
            <h3>{text.marketPrices}</h3>
          </div>
          <div className="card-data">
            {marketData?.featured && (
              <>
                <div className="data-item">
                  <strong>Commodity:</strong> 
                  <span>{language === 'hi' ? marketData.featured.commodity_hi : marketData.featured.commodity}</span>
                </div>
                <div className="data-item">
                  <strong>Price:</strong> 
                  <span className="price-highlight">{marketData.featured.price} / {marketData.featured.unit}</span>
                </div>
                <div className="data-item">
                  <strong>Location:</strong> 
                  <span>{language === 'hi' ? marketData.featured.location_hi : marketData.featured.location}</span>
                </div>
              </>
            )}
          </div>
          <p className="card-description">
            {language === 'hi' ? marketData?.description_hi : marketData?.description_en}
          </p>
        </div>

        {/* Weather Integration Card (reference to existing weather) */}
        <div className="insight-card weather-card">
          <div className="card-header">
            <TrendingUp size={40} className="card-icon" />
            <h3>{language === 'hi' ? 'मौसम और कीट' : 'Weather & Pests'}</h3>
          </div>
          <div className="card-data">
            <div className="data-item">
              <span>{language === 'hi' ? 'मौसम डेटा डैशबोर्ड पर उपलब्ध' : 'Weather data available on dashboard'}</span>
            </div>
          </div>
          <button className="view-weather-btn" onClick={() => navigate('/dashboard')}>
            {language === 'hi' ? 'मौसम देखें' : 'View Weather'}
          </button>
        </div>
      </div>

      {/* AI Recommendation Section */}
      {aiRecommendation && (
        <div className="ai-recommendation-section">
          <div className="recommendation-header">
            <Sparkles size={32} />
            <h2>{text.aiRecommendation}</h2>
          </div>
          <div className="recommendation-content">
            <p>{aiRecommendation.recommendation}</p>
          </div>
          <div className="recommendation-meta">
            <small>
              {language === 'hi' ? 'आधार: ' : 'Based on: '}
              pH {aiRecommendation.based_on?.soil_pH}, 
              NDVI {aiRecommendation.based_on?.ndvi}, 
              {language === 'hi' ? ' जोखिम: ' : ' Risk: '}{aiRecommendation.based_on?.weather_risk}
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmInsights;
